import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Bot, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Wifi,
  Settings,
  Terminal,
  Mic,
  Activity,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  Server,
  Key,
  RefreshCw,
  Users,
  Hash,
  Shield,
  Save
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const statusConfig = {
    online: { color: "bg-green-500", icon: CheckCircle2, text: "Online" },
    offline: { color: "bg-red-500", icon: XCircle, text: "Offline" },
    configured: { color: "bg-yellow-500", icon: AlertCircle, text: "Configured" },
    not_configured: { color: "bg-slate-500", icon: XCircle, text: "Not Configured" }
  };
  
  const config = statusConfig[status] || statusConfig.offline;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} text-white flex items-center gap-1.5 px-3 py-1`}>
      <Icon className="w-3.5 h-3.5" />
      {config.text}
    </Badge>
  );
};

const BotCard = ({ bot }) => {
  const [expanded, setExpanded] = useState(false);
  const BotIcon = bot.id === "analytics" ? Activity : Mic;
  
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              bot.status === "online" ? "bg-green-500/20 text-green-400" : 
              bot.status === "configured" ? "bg-yellow-500/20 text-yellow-400" :
              "bg-slate-700 text-slate-400"
            }`}>
              <BotIcon className="w-7 h-7" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white">{bot.name}</h3>
              <p className="text-slate-400 text-sm mt-0.5">{bot.description}</p>
            </div>
          </div>
          <StatusBadge status={bot.status} />
        </div>
        
        {bot.guild_name && (
          <div className="mt-4 flex items-center gap-2 text-slate-300">
            <Server className="w-4 h-4 text-slate-400" />
            <span>Connected to: <span className="text-purple-400 font-medium">{bot.guild_name}</span></span>
            {bot.member_count && (
              <span className="text-slate-500">({bot.member_count} members)</span>
            )}
          </div>
        )}
        
        <div className="mt-5">
          <h4 className="text-sm font-medium text-slate-300 mb-2">Features</h4>
          <div className="flex flex-wrap gap-2">
            {bot.features.map((feature, idx) => (
              <Badge key={idx} variant="outline" className="bg-slate-700/50 text-slate-300 border-slate-600">
                {feature}
              </Badge>
            ))}
          </div>
        </div>
        
        {bot.commands && bot.commands.length > 0 && (
          <div className="mt-5">
            <h4 className="text-sm font-medium text-slate-300 mb-2">Slash Commands</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {bot.commands.map((cmd, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-slate-900/50 px-3 py-2 rounded-lg">
                  <Terminal className="w-4 h-4 text-purple-400" />
                  <code className="text-purple-300">{cmd.name}</code>
                  <span className="text-slate-500 text-xs">- {cmd.description}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="border-t border-slate-700">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-6 py-3 flex items-center justify-between text-slate-400 hover:bg-slate-700/30 transition-colors"
        >
          <span className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Configuration Details
          </span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        {expanded && (
          <div className="px-6 pb-6 space-y-4">
            {bot.client_id && (
              <div className="flex items-center justify-between bg-slate-900/50 px-4 py-3 rounded-lg">
                <div>
                  <span className="text-slate-400 text-sm">Client ID</span>
                  <p className="text-white font-mono text-sm">{bot.client_id}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    navigator.clipboard.writeText(bot.client_id);
                    toast.success("Client ID copied!");
                  }}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {bot.config_keys && (
              <div>
                <span className="text-slate-400 text-sm">Environment Variables</span>
                <div className="mt-2 space-y-2">
                  {bot.config_keys.map((config, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-900/30 px-3 py-2 rounded">
                      <div className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-slate-500" />
                        <code className="text-slate-300 text-sm">{config.key}</code>
                      </div>
                      {config.set ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const PermissionManager = ({ token, botId, botName }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({
    allowed_users: [],
    allowed_channels: [],
    allowed_roles: []
  });

  const fetchData = async () => {
    if (!expanded) return;
    setLoading(true);
    try {
      const [membersRes, channelsRes, permissionsRes, rolesRes] = await Promise.all([
        axios.get(`${API}/discord/bots/members`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/discord/bots/channels`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/discord/bots/permissions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/discord/roles`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setMembers(membersRes.data.members || []);
      setChannels(channelsRes.data.channels || []);
      setRoles(rolesRes.data.roles || []);
      
      const botPerms = permissionsRes.data[botId] || { allowed_users: [], allowed_channels: [], allowed_roles: [] };
      setPermissions(botPerms);
    } catch (error) {
      console.error("Failed to fetch permission data:", error);
      toast.error("Failed to load permission data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded) {
      fetchData();
    }
  }, [expanded]);

  const toggleUser = (userId) => {
    setPermissions(prev => ({
      ...prev,
      allowed_users: prev.allowed_users.includes(userId)
        ? prev.allowed_users.filter(id => id !== userId)
        : [...prev.allowed_users, userId]
    }));
  };

  const toggleChannel = (channelId) => {
    setPermissions(prev => ({
      ...prev,
      allowed_channels: prev.allowed_channels.includes(channelId)
        ? prev.allowed_channels.filter(id => id !== channelId)
        : [...prev.allowed_channels, channelId]
    }));
  };

  const toggleRole = (roleName) => {
    setPermissions(prev => ({
      ...prev,
      allowed_roles: prev.allowed_roles.includes(roleName)
        ? prev.allowed_roles.filter(r => r !== roleName)
        : [...prev.allowed_roles, roleName]
    }));
  };

  const savePermissions = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/discord/bots/permissions`, {
        bot_id: botId,
        allowed_users: permissions.allowed_users,
        allowed_channels: permissions.allowed_channels,
        allowed_roles: permissions.allowed_roles
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success("Permissions saved successfully!");
    } catch (error) {
      console.error("Failed to save permissions:", error);
      toast.error("Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  // Filter voice channels for the transcription bot
  const voiceChannels = channels.filter(c => c.type === 'voice');

  return (
    <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden mt-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-700/20 transition-colors"
      >
        <span className="flex items-center gap-3 text-white font-medium">
          <Shield className="w-5 h-5 text-purple-400" />
          {botName} Permissions
        </span>
        {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      
      {expanded && (
        <div className="px-5 pb-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              <p className="text-slate-400 text-sm">
                Leave all sections empty to allow everyone. Select specific users, voice channels, or roles to restrict access.
              </p>
              
              {/* Allowed Roles */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-purple-400" />
                  Allowed Roles ({permissions.allowed_roles.length} selected)
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 bg-slate-900/30 rounded-lg">
                  {roles.map(role => (
                    <label 
                      key={role.id} 
                      className="flex items-center gap-2 p-2 rounded hover:bg-slate-700/50 cursor-pointer"
                    >
                      <Checkbox
                        checked={permissions.allowed_roles.includes(role.name)}
                        onCheckedChange={() => toggleRole(role.name)}
                      />
                      <span className="text-sm text-slate-300 truncate">{role.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Allowed Voice Channels */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                  <Mic className="w-4 h-4 text-purple-400" />
                  Allowed Voice Channels ({permissions.allowed_channels.length} selected)
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 bg-slate-900/30 rounded-lg">
                  {voiceChannels.map(channel => (
                    <label 
                      key={channel.id} 
                      className="flex items-center gap-2 p-2 rounded hover:bg-slate-700/50 cursor-pointer"
                    >
                      <Checkbox
                        checked={permissions.allowed_channels.includes(channel.id)}
                        onCheckedChange={() => toggleChannel(channel.id)}
                      />
                      <span className="text-sm text-slate-300 truncate">🔊 {channel.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Allowed Users */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-400" />
                  Allowed Users ({permissions.allowed_users.length} selected)
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto p-2 bg-slate-900/30 rounded-lg">
                  {members.map(member => (
                    <label 
                      key={member.id} 
                      className="flex items-center gap-2 p-2 rounded hover:bg-slate-700/50 cursor-pointer"
                    >
                      <Checkbox
                        checked={permissions.allowed_users.includes(member.id)}
                        onCheckedChange={() => toggleUser(member.id)}
                      />
                      <span className="text-sm text-slate-300 truncate">{member.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end pt-4 border-t border-slate-700">
                <Button
                  onClick={savePermissions}
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Permissions
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const InstructionSection = ({ title, icon: Icon, instructions }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-700/20 transition-colors"
      >
        <span className="flex items-center gap-3 text-white font-medium">
          <Icon className="w-5 h-5 text-purple-400" />
          {title}
        </span>
        {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      
      {expanded && (
        <div className="px-5 pb-5">
          <ol className="space-y-3">
            {instructions.map((step, idx) => (
              <li key={idx} className="flex gap-3 text-slate-300">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 text-sm flex items-center justify-center font-medium">
                  {idx + 1}
                </span>
                <span className="pt-0.5">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default function DiscordBots() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [botsData, setBotsData] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchBotsStatus = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);
      
      const response = await axios.get(`${API}/discord/bots/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBotsData(response.data);
    } catch (error) {
      console.error("Failed to fetch bots status:", error);
      if (error.response?.status === 403) {
        toast.error("You don't have permission to access this page");
        navigate("/");
      } else {
        toast.error("Failed to load Discord bots status");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBotsStatus();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-400">Loading Discord bots...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Discord Bots</h1>
                  <p className="text-slate-400 text-sm">Manage and monitor bot integrations</p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/discord-linking")}
                className="border-purple-600 text-purple-400 hover:bg-purple-700/20"
              >
                <Users className="w-4 h-4 mr-2" />
                Link Members
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchBotsStatus(true)}
                disabled={refreshing}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {botsData?.current_guild_id && (
          <div className="mb-6 bg-slate-800/30 border border-slate-700 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Server className="w-5 h-5 text-slate-400" />
              <div>
                <span className="text-slate-400 text-sm">Current Discord Server ID</span>
                <p className="text-white font-mono">{botsData.current_guild_id}</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                navigator.clipboard.writeText(botsData.current_guild_id);
                toast.success("Guild ID copied!");
              }}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        )}
        
        <div className="space-y-6 mb-10">
          <h2 className="text-lg font-semibold text-slate-200 flex items-center gap-2">
            <Wifi className="w-5 h-5 text-purple-400" />
            Bot Status
          </h2>
          
          <div className="grid gap-6">
            {botsData?.bots?.map((bot) => (
              <div key={bot.id}>
                <BotCard bot={bot} />
                {bot.id === "transcription" && (
                  <PermissionManager 
                    token={token} 
                    botId="transcription_bot" 
                    botName="Voice Transcription Bot"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        
        {botsData?.setup_instructions && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-200 flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-400" />
              Setup Instructions
            </h2>
            
            <div className="space-y-3">
              <InstructionSection
                title="How to Change Discord Server"
                icon={Server}
                instructions={botsData.setup_instructions.change_server}
              />
              
              <InstructionSection
                title="How to Get a Server's Guild ID"
                icon={Key}
                instructions={botsData.setup_instructions.get_guild_id}
              />
              
              <InstructionSection
                title="How to Create a New Bot"
                icon={Bot}
                instructions={botsData.setup_instructions.create_new_bot}
              />
            </div>
            
            <a
              href="https://discord.com/developers/applications"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors mt-4"
            >
              <ExternalLink className="w-4 h-4" />
              Open Discord Developer Portal
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
